import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, Mail, AlertCircle, Loader2, LogOut, Zap, Trash2, CheckCircle, X, FileText, Inbox, Image as ImageIcon, Download, Calendar, Plus, Edit3, Save, MessageSquareText, Send,
} from "lucide-react";

const STATUS_OPTIONS = ["new", "reviewed", "accepted", "rejected"];
const PHOTO_STATUS_OPTIONS = ["new", "posted", "rejected"];
const EVENT_TYPES = ["Meet & Greet", "Chase Season Kickoff", "Public Briefing", "Gear Demo", "Fundraiser", "Other"];

function Stat({ label, value, accent }) {
  return (
    <div className="card-vg p-6">
      <div className="overline">{label}</div>
      <div className={`font-heading text-4xl font-black mt-3 font-mono ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    new: "bg-yellow-400/10 text-yellow-400 border-yellow-400/40",
    reviewed: "bg-sky-400/10 text-sky-400 border-sky-400/40",
    accepted: "bg-green-500/10 text-green-500 border-green-500/40",
    rejected: "bg-red-500/10 text-red-500 border-red-500/40",
  };
  return (
    <span className={`inline-block text-[10px] font-mono uppercase tracking-wider border px-2 py-1 ${styles[status] || styles.new}`}>
      {status}
    </span>
  );
}

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("applications");
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [events, setEvents] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [smsStatus, setSmsStatus] = useState(null);
  const [broadcastMsg, setBroadcastMsg] = useState("");
  const [broadcasting, setBroadcasting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [eventForm, setEventForm] = useState(null); // {id?, data}

  const fetchAll = useCallback(async () => {
    setBusy(true);
    try {
      const [s, a, c, p, e, subs, sms] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/applications"),
        api.get("/contacts"),
        api.get("/photos"),
        api.get("/events", { params: { include_past: true } }),
        api.get("/sms/subscribers"),
        api.get("/sms/status"),
      ]);
      setStats(s.data);
      setApps(a.data);
      setContacts(c.data);
      setPhotos(p.data);
      setEvents(e.data);
      setSubscribers(subs.data);
      setSmsStatus(sms.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchAll();
  }, [user, fetchAll]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-yellow-400 animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const updateAppStatus = async (id, status) => {
    try {
      await api.patch(`/applications/${id}/status`, null, { params: { status } });
      toast.success(`Marked ${status}`);
      fetchAll();
      setSelected(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteApp = async (id) => {
    if (!window.confirm("Delete this application permanently?")) return;
    try {
      await api.delete(`/applications/${id}`);
      toast.success("Deleted");
      fetchAll();
      setSelected(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const markContact = async (id, read) => {
    try {
      await api.patch(`/contacts/${id}/read`, null, { params: { read } });
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this contact message?")) return;
    try {
      await api.delete(`/contacts/${id}`);
      toast.success("Deleted");
      fetchAll();
      setSelected(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openPhoto = async (photo) => {
    try {
      const { data } = await api.get(`/photos/${photo.id}/image`);
      setSelected({
        type: "photo",
        data: photo,
        image: `data:${data.mime_type};base64,${data.data_base64}`,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updatePhotoStatus = async (id, status) => {
    try {
      await api.patch(`/photos/${id}/status`, null, { params: { status } });
      toast.success(`Marked ${status}`);
      fetchAll();
      setSelected(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm("Delete this photo submission permanently?")) return;
    try {
      await api.delete(`/photos/${id}`);
      toast.success("Deleted");
      fetchAll();
      setSelected(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ---- Events ----
  const openNewEvent = () => setEventForm({
    id: null,
    data: {
      title: "",
      description: "",
      location: "",
      starts_at: "",
      ends_at: "",
      event_type: "Meet & Greet",
      rsvp_url: "",
      cover_image_base64: null,
      cover_image_mime: null,
      cover_filename: null,
    },
  });

  const openEditEvent = (ev) => setEventForm({
    id: ev.id,
    data: {
      title: ev.title || "",
      description: ev.description || "",
      location: ev.location || "",
      starts_at: ev.starts_at ? isoToLocalInput(ev.starts_at) : "",
      ends_at: ev.ends_at ? isoToLocalInput(ev.ends_at) : "",
      event_type: ev.event_type || "Meet & Greet",
      rsvp_url: ev.rsvp_url || "",
      cover_image_base64: null,
      cover_image_mime: null,
      cover_filename: null,
    },
  });

  const saveEvent = async () => {
    if (!eventForm) return;
    const d = eventForm.data;
    if (!d.title || !d.description || !d.location || !d.starts_at) {
      toast.error("Please fill title, description, location, and start time");
      return;
    }
    const payload = {
      title: d.title.trim(),
      description: d.description.trim(),
      location: d.location.trim(),
      starts_at: localInputToIso(d.starts_at),
      ends_at: d.ends_at ? localInputToIso(d.ends_at) : null,
      event_type: d.event_type,
      rsvp_url: d.rsvp_url || null,
    };
    if (d.cover_image_base64) {
      payload.cover_image_base64 = d.cover_image_base64;
      payload.cover_image_mime = d.cover_image_mime;
    }
    try {
      if (eventForm.id) {
        await api.patch(`/events/${eventForm.id}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/events", payload);
        toast.success("Event created");
      }
      setEventForm(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event permanently?")) return;
    try {
      await api.delete(`/events/${id}`);
      toast.success("Deleted");
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteSubscriber = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try {
      await api.delete(`/sms/subscribers/${id}`);
      toast.success("Subscriber removed");
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendBroadcast = async () => {
    const message = broadcastMsg.trim();
    if (!message) return;
    const active = subscribers.filter((s) => s.active).length;
    if (!window.confirm(`Send this text to ${active} subscriber${active === 1 ? "" : "s"}?`)) return;
    setBroadcasting(true);
    try {
      const { data } = await api.post("/sms/broadcast", { message });
      toast.success(`Broadcast sent: ${data.sent}/${data.total} delivered${data.failed ? `, ${data.failed} failed` : ""}`);
      setBroadcastMsg("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBroadcasting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-page">
      {/* Top bar */}
      <header className="border-b border-white/5 bg-[#0d0f12]">
        <div className="container-vg flex items-center justify-between h-20">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 border border-yellow-400/40 bg-yellow-400/5">
              <Zap className="w-5 h-5 text-yellow-400" strokeWidth={2.5} />
            </span>
            <div className="leading-none font-heading">
              <div className="text-white font-black text-base uppercase tracking-tight">Vortex Ghost</div>
              <div className="text-[10px] tracking-[0.28em] text-zinc-500 font-mono uppercase mt-1">Operator Console</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:block text-right">
              <div className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Logged in as</div>
              <div className="text-sm text-white font-mono">{user.email}</div>
            </div>
            <button onClick={logout} className="btn-secondary !py-2 !px-4 !text-xs" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container-vg py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-10">
          <Stat label="Total Applications" value={stats?.applications_total ?? "—"} />
          <Stat label="New Applications" value={stats?.applications_new ?? "—"} accent="text-yellow-400" />
          <Stat label="Total Messages" value={stats?.contacts_total ?? "—"} />
          <Stat label="Unread Messages" value={stats?.contacts_unread ?? "—"} accent="text-sky-400" />
          <Stat label="Total Photos" value={stats?.photos_total ?? "—"} />
          <Stat label="New Photos" value={stats?.photos_new ?? "—"} accent="text-yellow-400" />
          <Stat label="Total Events" value={stats?.events_total ?? "—"} />
          <Stat label="Upcoming Events" value={stats?.events_upcoming ?? "—"} accent="text-yellow-400" />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-white/10 mb-8">
          <button
            onClick={() => setTab("applications")}
            className={`flex items-center gap-2 px-5 py-3 font-mono text-xs uppercase tracking-wider border-b-2 -mb-px ${
              tab === "applications" ? "border-yellow-400 text-yellow-400" : "border-transparent text-zinc-400 hover:text-white"
            }`}
            data-testid="admin-tab-applications"
          >
            <Users className="w-4 h-4" /> Applications ({apps.length})
          </button>
          <button
            onClick={() => setTab("contacts")}
            className={`flex items-center gap-2 px-5 py-3 font-mono text-xs uppercase tracking-wider border-b-2 -mb-px ${
              tab === "contacts" ? "border-yellow-400 text-yellow-400" : "border-transparent text-zinc-400 hover:text-white"
            }`}
            data-testid="admin-tab-contacts"
          >
            <Mail className="w-4 h-4" /> Messages ({contacts.length})
          </button>
          <button
            onClick={() => setTab("photos")}
            className={`flex items-center gap-2 px-5 py-3 font-mono text-xs uppercase tracking-wider border-b-2 -mb-px ${
              tab === "photos" ? "border-yellow-400 text-yellow-400" : "border-transparent text-zinc-400 hover:text-white"
            }`}
            data-testid="admin-tab-photos"
          >
            <ImageIcon className="w-4 h-4" /> Photos ({photos.length})
          </button>
          <button
            onClick={() => setTab("events")}
            className={`flex items-center gap-2 px-5 py-3 font-mono text-xs uppercase tracking-wider border-b-2 -mb-px ${
              tab === "events" ? "border-yellow-400 text-yellow-400" : "border-transparent text-zinc-400 hover:text-white"
            }`}
            data-testid="admin-tab-events"
          >
            <Calendar className="w-4 h-4" /> Events ({events.length})
          </button>
          <button
            onClick={() => setTab("sms")}
            className={`flex items-center gap-2 px-5 py-3 font-mono text-xs uppercase tracking-wider border-b-2 -mb-px ${
              tab === "sms" ? "border-yellow-400 text-yellow-400" : "border-transparent text-zinc-400 hover:text-white"
            }`}
            data-testid="admin-tab-sms"
          >
            <MessageSquareText className="w-4 h-4" /> SMS Alerts ({subscribers.filter((s) => s.active).length})
          </button>
        </div>

        {busy && (
          <div className="flex items-center gap-3 text-zinc-400 mb-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {tab === "applications" && (
          <div data-testid="applications-list">
            {apps.length === 0 ? (
              <EmptyState icon={Inbox} label="No applications yet" />
            ) : (
              <div className="border border-white/5 divide-y divide-white/5">
                {apps.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => setSelected({ type: "application", data: a })}
                    className="w-full flex flex-col md:flex-row md:items-center gap-3 p-5 hover:bg-white/[0.02] text-left"
                    data-testid={`application-row-${a.id}`}
                  >
                    <div className="flex-1">
                      <div className="font-heading text-lg text-white">{a.full_name}</div>
                      <div className="text-xs text-zinc-500 font-mono mt-1">{a.email} · {a.phone} · {a.region}</div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-zinc-500 font-mono">{new Date(a.created_at).toLocaleDateString()}</span>
                      <span className="text-xs text-yellow-400 font-mono uppercase tracking-wider">{a.role || "—"}</span>
                      <span className="text-xs text-sky-400 font-mono uppercase">{a.experience_level}</span>
                      {a.has_resume && <FileText className="w-4 h-4 text-yellow-400" title="Resume attached" />}
                      <StatusBadge status={a.status} />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "contacts" && (
          <div data-testid="contacts-list">
            {contacts.length === 0 ? (
              <EmptyState icon={Inbox} label="No messages yet" />
            ) : (
              <div className="border border-white/5 divide-y divide-white/5">
                {contacts.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => {
                      setSelected({ type: "contact", data: c });
                      if (!c.read) markContact(c.id, true);
                    }}
                    className={`w-full flex flex-col md:flex-row md:items-center gap-3 p-5 hover:bg-white/[0.02] text-left ${
                      !c.read ? "bg-yellow-400/[0.03]" : ""
                    }`}
                    data-testid={`contact-row-${c.id}`}
                  >
                    <div className="flex-1">
                      <div className="font-heading text-lg text-white flex items-center gap-2">
                        {!c.read && <span className="w-2 h-2 rounded-full bg-yellow-400" />}
                        {c.subject}
                      </div>
                      <div className="text-xs text-zinc-500 font-mono mt-1">From {c.name} · {c.email}</div>
                    </div>
                    <div className="text-xs text-zinc-500 font-mono">{new Date(c.created_at).toLocaleDateString()}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "photos" && (
          <div data-testid="photos-list">
            {photos.length === 0 ? (
              <EmptyState icon={ImageIcon} label="No photos submitted yet" />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {photos.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => openPhoto(p)}
                    className="card-vg p-0 overflow-hidden text-left group"
                    data-testid={`photo-card-${p.id}`}
                  >
                    <div className="aspect-square bg-gradient-to-br from-[#1A1D24] to-[#0d0f12] flex items-center justify-center relative group-hover:from-[#22262e]">
                      <ImageIcon className="w-12 h-12 text-zinc-600 group-hover:text-yellow-400 transition-colors" />
                      <span className="absolute top-2 left-2">
                        <PhotoStatusBadge status={p.status} />
                      </span>
                      <span className="absolute bottom-2 right-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                        {new Date(p.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="p-4">
                      <div className="font-heading text-sm font-semibold text-white truncate group-hover:text-yellow-400">{p.photographer_name}</div>
                      <div className="text-xs text-zinc-500 font-mono mt-1 truncate">{p.location}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "events" && (
          <div data-testid="events-list">
            <div className="flex items-center justify-between mb-6">
              <div className="text-sm text-zinc-400 font-mono uppercase tracking-wider">
                {events.length} event{events.length === 1 ? "" : "s"} total
              </div>
              <button onClick={openNewEvent} className="btn-primary !py-2 !px-4 !text-xs" data-testid="admin-event-new">
                <Plus className="w-4 h-4" /> New Event
              </button>
            </div>
            {events.length === 0 ? (
              <EmptyState icon={Calendar} label="No events yet — add your first" />
            ) : (
              <div className="border border-white/5 divide-y divide-white/5">
                {events.map((ev) => {
                  const upcoming = new Date(ev.ends_at || ev.starts_at) >= new Date();
                  return (
                    <div key={ev.id} className="p-5 flex flex-col md:flex-row gap-4 md:items-center" data-testid={`admin-event-row-${ev.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="overline">{ev.event_type}</span>
                          {!upcoming && <span className="text-[10px] font-mono uppercase tracking-wider bg-zinc-700 text-zinc-300 px-2 py-0.5">Past</span>}
                          {upcoming && <span className="text-[10px] font-mono uppercase tracking-wider bg-yellow-400 text-black px-2 py-0.5">Upcoming</span>}
                        </div>
                        <div className="font-heading text-lg text-white mt-1">{ev.title}</div>
                        <div className="text-xs text-zinc-500 font-mono mt-1">
                          {new Date(ev.starts_at).toLocaleString()} · {ev.location}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditEvent(ev)}
                          className="text-xs font-mono uppercase tracking-wider border border-white/10 hover:border-yellow-400 hover:text-yellow-400 text-zinc-400 px-3 py-2 inline-flex items-center gap-2"
                          data-testid={`admin-event-edit-${ev.id}`}
                        >
                          <Edit3 className="w-3 h-3" /> Edit
                        </button>
                        <button
                          onClick={() => deleteEvent(ev.id)}
                          className="text-xs font-mono uppercase tracking-wider border border-red-500/30 hover:bg-red-500/10 text-red-400 px-3 py-2 inline-flex items-center gap-2"
                          data-testid={`admin-event-delete-${ev.id}`}
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === "sms" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="sms-panel">
            {/* Broadcast composer */}
            <div className="card-vg p-6">
              <div className="overline mb-4">Broadcast to Subscribers</div>
              {smsStatus && !smsStatus.configured && (
                <div className="border border-yellow-400/40 bg-yellow-400/5 p-4 mb-4 text-xs text-zinc-300 font-mono leading-relaxed" data-testid="sms-not-configured">
                  TWILIO NOT CONFIGURED — add TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN and TWILIO_PHONE_NUMBER to enable texting.
                  {smsStatus.admin_alert_phone_set ? "" : " ADMIN_ALERT_PHONE is also empty, so you won't get submission alerts yet."}
                </div>
              )}
              {smsStatus?.configured && (
                <div className="border border-green-500/30 bg-green-500/5 p-3 mb-4 text-xs text-green-400 font-mono" data-testid="sms-configured">
                  Twilio connected · sending from {smsStatus.from_number}
                  {smsStatus.admin_alert_phone_set ? " · admin alerts on" : " · ADMIN_ALERT_PHONE empty (no submission alerts)"}
                </div>
              )}
              <textarea
                value={broadcastMsg}
                onChange={(e) => setBroadcastMsg(e.target.value)}
                rows={5}
                maxLength={900}
                placeholder="e.g. CHASE DAY — target Woodward, OK. Departing ops base 11:00. Live on radar page."
                className="w-full bg-[#0d0f12] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-yellow-400/60 focus:outline-none resize-none"
                data-testid="sms-broadcast-input"
              />
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-zinc-500 font-mono">{broadcastMsg.length}/900 · {subscribers.filter((s) => s.active).length} active recipients</span>
                <button
                  onClick={sendBroadcast}
                  disabled={broadcasting || !broadcastMsg.trim()}
                  className="btn-primary !py-2 !px-5 !text-xs flex items-center gap-2 disabled:opacity-50"
                  data-testid="sms-broadcast-send"
                >
                  {broadcasting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send Broadcast
                </button>
              </div>
            </div>

            {/* Subscriber list */}
            <div className="card-vg p-6">
              <div className="overline mb-4">Subscribers ({subscribers.length})</div>
              {subscribers.length === 0 ? (
                <EmptyState icon={MessageSquareText} label="No subscribers yet — share the signup form in the footer" />
              ) : (
                <div className="border border-white/5 divide-y divide-white/5 max-h-[420px] overflow-y-auto">
                  {subscribers.map((s) => (
                    <div key={s.id} className="flex items-center justify-between gap-3 p-4" data-testid={`subscriber-row-${s.id}`}>
                      <div>
                        <div className="text-sm text-white font-mono">{s.phone}</div>
                        <div className="text-xs text-zinc-500 font-mono mt-0.5">
                          {s.name || "—"} · {new Date(s.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-[10px] font-mono uppercase tracking-wider border px-2 py-1 ${
                          s.active ? "bg-green-500/10 text-green-500 border-green-500/40" : "bg-zinc-500/10 text-zinc-500 border-zinc-500/40"
                        }`}>
                          {s.active ? "active" : "opted out"}
                        </span>
                        <button onClick={() => deleteSubscriber(s.id)} className="text-zinc-500 hover:text-red-400" data-testid={`subscriber-delete-${s.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/70 backdrop-blur-sm" onClick={() => setSelected(null)} data-testid="admin-drawer">
          <div className="w-full max-w-2xl bg-[#0d0f12] border-l border-white/10 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-[#0d0f12] border-b border-white/10 p-5 flex items-center justify-between">
              <div className="overline">{selected.type === "application" ? "Application Detail" : selected.type === "contact" ? "Message Detail" : "Photo Submission"}</div>
              <button onClick={() => setSelected(null)} className="text-zinc-400 hover:text-white" data-testid="drawer-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-7 space-y-6">
              {selected.type === "application" ? (
                <ApplicationDetail
                  app={selected.data}
                  onStatus={updateAppStatus}
                  onDelete={deleteApp}
                />
              ) : selected.type === "contact" ? (
                <ContactDetail msg={selected.data} onDelete={deleteContact} onMarkUnread={(id) => markContact(id, false)} />
              ) : (
                <PhotoDetail
                  photo={selected.data}
                  image={selected.image}
                  onStatus={updatePhotoStatus}
                  onDelete={deletePhoto}
                />
              )}
            </div>
          </div>
        </div>
      )}
      {/* Event form modal */}
      {eventForm && (
        <EventFormModal
          form={eventForm}
          setForm={setEventForm}
          onSave={saveEvent}
          onClose={() => setEventForm(null)}
        />
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, label }) {
  return (
    <div className="border border-white/5 p-20 text-center">
      <Icon className="w-10 h-10 text-zinc-600 mx-auto" />
      <div className="text-zinc-500 mt-4 font-mono uppercase tracking-wider text-xs">{label}</div>
    </div>
  );
}

function ApplicationDetail({ app, onStatus, onDelete }) {
  return (
    <>
      <div>
        <h2 className="font-heading text-3xl font-bold">{app.full_name}</h2>
        <div className="text-sm text-zinc-400 font-mono mt-2">{app.email} · {app.phone}</div>
        <div className="mt-3"><StatusBadge status={app.status} /></div>
      </div>

      <DetailGrid items={[
        ["Role", app.role || "—"],
        ["Experience", app.experience_level],
        ["Region", app.region],
        ["Chase vehicle", app.chase_vehicle],
        ["Availability", app.availability],
      ]} />

      <DetailSection title="Why they want to join">{app.why_join}</DetailSection>
      <DetailSection title="Equipment">{app.equipment}</DetailSection>
      <DetailSection title="Emergency contact">
        {app.emergency_contact_name} — {app.emergency_contact_phone}
      </DetailSection>

      {app.resume_filename && (
        <DetailSection title="Resume">
          <div className="flex items-center gap-2 text-yellow-400 font-mono text-sm">
            <FileText className="w-4 h-4" /> {app.resume_filename}
          </div>
        </DetailSection>
      )}

      <div className="pt-6 border-t border-white/10">
        <div className="overline mb-3">Update Status</div>
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => onStatus(app.id, s)}
              disabled={app.status === s}
              className={`text-xs font-mono uppercase tracking-wider border px-3 py-2 ${
                app.status === s
                  ? "border-yellow-400 text-yellow-400 bg-yellow-400/5 cursor-default"
                  : "border-white/10 text-zinc-400 hover:border-yellow-400 hover:text-yellow-400"
              }`}
              data-testid={`app-status-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-end pt-6 border-t border-white/10">
        <button onClick={() => onDelete(app.id)} className="text-red-400 hover:text-red-300 flex items-center gap-2 text-xs font-mono uppercase tracking-wider" data-testid="app-delete-btn">
          <Trash2 className="w-4 h-4" /> Delete application
        </button>
      </div>
    </>
  );
}

function ContactDetail({ msg, onDelete, onMarkUnread }) {
  return (
    <>
      <div>
        <h2 className="font-heading text-2xl font-bold">{msg.subject}</h2>
        <div className="text-sm text-zinc-400 font-mono mt-2">
          From {msg.name} · {msg.email}
        </div>
        <div className="text-xs text-zinc-500 mt-1">{new Date(msg.created_at).toLocaleString()}</div>
      </div>

      <DetailSection title="Message">{msg.message}</DetailSection>

      <div className="flex flex-wrap items-center gap-3 justify-between pt-6 border-t border-white/10">
        <button onClick={() => onMarkUnread(msg.id)} className="text-xs font-mono uppercase tracking-wider text-zinc-400 hover:text-yellow-400 flex items-center gap-2" data-testid="msg-mark-unread">
          <CheckCircle className="w-4 h-4" /> Mark unread
        </button>
        <button onClick={() => onDelete(msg.id)} className="text-red-400 hover:text-red-300 flex items-center gap-2 text-xs font-mono uppercase tracking-wider" data-testid="msg-delete-btn">
          <Trash2 className="w-4 h-4" /> Delete
        </button>
      </div>
    </>
  );
}

function PhotoDetail({ photo, image, onStatus, onDelete }) {
  return (
    <>
      <div className="border border-white/10 bg-black">
        {image ? (
          <img src={image} alt={photo.caption} className="w-full max-h-[520px] object-contain" data-testid="photo-full-image" />
        ) : (
          <div className="aspect-video flex items-center justify-center"><Loader2 className="w-6 h-6 text-yellow-400 animate-spin" /></div>
        )}
      </div>

      <div>
        <h2 className="font-heading text-2xl font-bold">{photo.photographer_name}</h2>
        <div className="text-sm text-zinc-400 font-mono mt-2">{photo.email}</div>
        <div className="mt-3"><PhotoStatusBadge status={photo.status} /></div>
      </div>

      <DetailGrid items={[
        ["Location", photo.location],
        ["Instagram", photo.instagram_handle || "—"],
        ["Filename", photo.photo_filename],
        ["Submitted", new Date(photo.created_at).toLocaleString()],
      ]} />

      <DetailSection title="Caption / Story">{photo.caption}</DetailSection>

      <div className="pt-6 border-t border-white/10">
        <div className="overline mb-3">Update Status</div>
        <div className="flex flex-wrap gap-2">
          {PHOTO_STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => onStatus(photo.id, s)}
              disabled={photo.status === s}
              className={`text-xs font-mono uppercase tracking-wider border px-3 py-2 ${
                photo.status === s
                  ? "border-yellow-400 text-yellow-400 bg-yellow-400/5 cursor-default"
                  : "border-white/10 text-zinc-400 hover:border-yellow-400 hover:text-yellow-400"
              }`}
              data-testid={`photo-status-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-6 border-t border-white/10">
        {image && (
          <a
            href={image}
            download={photo.photo_filename}
            className="text-yellow-400 hover:text-yellow-300 flex items-center gap-2 text-xs font-mono uppercase tracking-wider"
            data-testid="photo-download-btn"
          >
            <Download className="w-4 h-4" /> Download photo
          </a>
        )}
        <button onClick={() => onDelete(photo.id)} className="text-red-400 hover:text-red-300 flex items-center gap-2 text-xs font-mono uppercase tracking-wider ml-auto" data-testid="photo-delete-btn">
          <Trash2 className="w-4 h-4" /> Delete
        </button>
      </div>
    </>
  );
}

function PhotoStatusBadge({ status }) {
  const styles = {
    new: "bg-yellow-400/10 text-yellow-400 border-yellow-400/40",
    posted: "bg-green-500/10 text-green-500 border-green-500/40",
    rejected: "bg-red-500/10 text-red-500 border-red-500/40",
  };
  return (
    <span className={`inline-block text-[10px] font-mono uppercase tracking-wider border px-2 py-1 ${styles[status] || styles.new}`}>
      {status}
    </span>
  );
}

function DetailGrid({ items }) {
  return (
    <div className="grid grid-cols-2 gap-4 border border-white/5 p-5">
      {items.map(([k, v]) => (
        <div key={k}>
          <div className="overline">{k}</div>
          <div className="text-sm text-white mt-1">{v || "—"}</div>
        </div>
      ))}
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div>
      <div className="overline mb-2">{title}</div>
      <div className="text-zinc-200 text-sm leading-relaxed whitespace-pre-line">{children}</div>
    </div>
  );
}


// Convert ISO string → yyyy-MM-ddTHH:mm (browser datetime-local format, local TZ)
function isoToLocalInput(iso) {
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

// Convert datetime-local (local TZ) → ISO string (UTC)
function localInputToIso(input) {
  if (!input) return null;
  return new Date(input).toISOString();
}

function EventFormModal({ form, setForm, onSave, onClose }) {
  const isEdit = !!form.id;
  const d = form.data;
  const update = (k, v) => setForm((f) => ({ ...f, data: { ...f.data, [k]: v } }));

  const handleCover = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Cover must be under 5 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const b64 = typeof result === "string" ? result.split(",")[1] : null;
      update("cover_image_base64", b64);
      update("cover_image_mime", file.type);
      update("cover_filename", file.name);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-6 overflow-y-auto" onClick={onClose} data-testid="event-form-modal">
      <div className="w-full max-w-2xl bg-[#0d0f12] border border-white/10 mt-16 mb-16" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="overline">{isEdit ? "Edit Event" : "New Event"}</div>
            <div className="font-heading text-xl font-bold mt-1">
              {isEdit ? "Update event details" : "Add a meet & greet or briefing"}
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="event-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 md:p-8 space-y-5">
          <div>
            <label className="overline mb-2 block !text-zinc-400">Title *</label>
            <input value={d.title} onChange={(e) => update("title", e.target.value)} className="input-vg" placeholder="e.g., Norman Meet & Greet" data-testid="event-field-title" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="overline mb-2 block !text-zinc-400">Event Type</label>
              <select value={d.event_type} onChange={(e) => update("event_type", e.target.value)} className="input-vg" data-testid="event-field-type">
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="overline mb-2 block !text-zinc-400">Location *</label>
              <input value={d.location} onChange={(e) => update("location", e.target.value)} className="input-vg" placeholder="Norman, OK" data-testid="event-field-location" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="overline mb-2 block !text-zinc-400">Start *</label>
              <input type="datetime-local" value={d.starts_at} onChange={(e) => update("starts_at", e.target.value)} className="input-vg" data-testid="event-field-starts" />
            </div>
            <div>
              <label className="overline mb-2 block !text-zinc-400">End (optional)</label>
              <input type="datetime-local" value={d.ends_at} onChange={(e) => update("ends_at", e.target.value)} className="input-vg" data-testid="event-field-ends" />
            </div>
          </div>

          <div>
            <label className="overline mb-2 block !text-zinc-400">Description *</label>
            <textarea rows={6} value={d.description} onChange={(e) => update("description", e.target.value)} className="input-vg resize-none" placeholder="What is it? What to expect? Anything attendees should bring?" data-testid="event-field-description" />
          </div>

          <div>
            <label className="overline mb-2 block !text-zinc-400">RSVP / Ticket URL (optional)</label>
            <input value={d.rsvp_url} onChange={(e) => update("rsvp_url", e.target.value)} className="input-vg" placeholder="https://..." data-testid="event-field-rsvp" />
          </div>

          <div>
            <label className="overline mb-2 block !text-zinc-400">Cover Image (optional, replaces existing)</label>
            <label className="block border-2 border-dashed border-white/15 hover:border-yellow-400 transition-colors p-6 text-center cursor-pointer" data-testid="event-cover-dropzone">
              <input type="file" className="hidden" accept="image/*" onChange={handleCover} data-testid="event-cover-input" />
              <div className="text-sm text-zinc-300">{d.cover_filename || "Click to upload"}</div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-1">
                JPG / PNG · max 5MB
              </div>
            </label>
          </div>
        </div>

        <div className="p-5 border-t border-white/10 flex justify-between items-center">
          <button onClick={onClose} className="text-xs font-mono uppercase tracking-wider text-zinc-400 hover:text-white" data-testid="event-form-cancel">
            Cancel
          </button>
          <button onClick={onSave} className="btn-primary" data-testid="event-form-save">
            <Save className="w-4 h-4" /> {isEdit ? "Save changes" : "Create event"}
          </button>
        </div>
      </div>
    </div>
  );
}
